import { PersonalHistory } from './../../types';
import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators';
import { ITranscription, Entry, ITranscriptionHistory, IRect, IDictEntry, ITaggedRect } from '@/types';
import { db } from '@/plugins/firebase';
import firebase from '@/plugins/firebase';
import { bindFirebaseRef, unbindFirebaseRef } from '@/plugins/bindFirebaseRef';
import store from '@/store/store';
import { IAnnotation, ITag, User, editingStatus } from '../../types';
import { log } from '@/plugins/logger';

@Module({ namespaced: true, name: 'transcription', store: store })
export default class Transcription extends VuexModule {
	entryId = '';
	transcriptionId = '';
	entry: Entry = {
		id: '',
		projectId: '',
		collectionId: '',
		label: '',
		size: 0,
		license: '',
		logo: '',
		manifestUrl: '',
		metadata: '',
		progress: 0,
		thumbnail: '',
		canvases: [],
		related: "",
		r18: false
	};
	transcription: ITranscription = {
		id: '',
		editedBy: '',
		tempEditedBy: '',
		entryId: '',
		index: 0,
		prevStatus: 'default',
		status: 'default',
		text: '',
		tempText: '',
		tempTextChanged: false,
		timestamp: firebase.firestore.FieldValue.serverTimestamp(),
		annotations: [],
		tempAnnotations: [],
		share: true,
		requestReview: false,
		syncMode: false
	};
	transcriptions: ITranscription[] = [];
	historyList: ITranscriptionHistory[] = [];
	currentIndex = 0;
	annotations: IAnnotation[] = [];

	@Mutation
	UPDATE_TRANSCRIPTION_LOCALLY(data: any) {
		for (let key in data) {
			(<any>this.transcription)[key] = data[key];
		}
	}

	@Mutation
	SET_CURRENT_INDEX(index: number) {
		this.currentIndex = index;
	}

	@Mutation
	SET_ENTRY_ID(entryId: string) {
		this.entryId = entryId;
	}

	@Mutation
	SET_TRSC_ID(trscId: string) {
		this.transcriptionId = trscId;
	}

	get currentTranscription() {
		return this.transcription;
	}

	get currentUser(): User {
		return this.context.rootState.auth.user;
	}

	get isEditable(): boolean {
		if (!this.currentUser) {
			return false;
		} else if (!this.transcription) {
			return false;
		} else if (
			this.transcription.status === 'editing' &&
			this.currentUser.uid !== this.transcription.tempEditedBy
		) {
			return false;
		} else if (this.transcription.status === 'frozen') {
			return false;
		} else {
			return true;
		}
	}

	get statusLabel(): string {
		if (!this.currentTranscription) return '編集不可';
		if (this.currentTranscription.status === 'editing') {
			const currentUser = this.context.rootState.auth.user;
			if (!currentUser) return '他ユーザーが編集中';
			return this.isCurrentUserEditing ? 'あなたが編集中' : '他ユーザーが編集中';
		} else {
			switch (this.currentTranscription.status) {
				case 'default':
					return '未着手';
				case 'completed':
					return '翻刻完了';
				case 'frozen':
					return '確定';
				case 'initiated':
					return '着手済み';
				default:
					return '不明';
			}
		}
	}

	get isCurrentUserEditing() {
		if (!this.transcription) return false;
		return (
			this.currentUser &&
			this.transcription.status === 'editing' &&
			this.currentUser.uid === this.transcription.tempEditedBy
		);
	}

	get isSyncMode() {
		if (!this.transcription) return false;
		return this.transcription.status === 'editing' && this.transcription.syncMode;
	}

	get history() {
		return this.historyList;
	}

	@Action
	async bindEntry(entryId: string) {
		this.SET_ENTRY_ID(entryId);
		const entryRef = db.collection('entries').doc(entryId);
		await bindFirebaseRef(this.context, 'entry', entryRef);
		const transcriptionRef = db
			.collection('transcriptions')
			.where('entryId', '==', entryId)
			.orderBy('index', 'asc');
		return await bindFirebaseRef(this.context, 'transcriptions', transcriptionRef);
	}

	@Action
	async unbindEntry() {
		return await unbindFirebaseRef(this.context.commit, 'entry');
	}

	@Action
	async unbindTranscription() {
		return await unbindFirebaseRef(this.context, 'transcription');
	}

	@Action
	async bindTranscription(payload: { entryId: string; index: number; }) {
		unbindFirebaseRef(this.context, 'transcription');
		const transcriptionId = `${payload.entryId}_${payload.index}`;
		this.SET_TRSC_ID(transcriptionId);
		const ref = db.collection('transcriptions').doc(transcriptionId);
		const historyRef = ref.collection('history').orderBy('timestamp', 'desc');
		bindFirebaseRef(this.context, 'historyList', historyRef);
		const annotationsRef = db.collection('annotations').where('transcriptionId', '==', transcriptionId);
		bindFirebaseRef(this.context, 'annotations', annotationsRef);
		return await bindFirebaseRef(this.context, 'transcription', ref);
	}

	// mutates local transcription
	@Action({ commit: 'UPDATE_TRANSCRIPTION_LOCALLY' })
	updateTranscriptionLocally(data: Partial<ITranscription>) {
		return data;
	}

	// updates both remote and local transcription
	//@Action({ commit: 'UPDATE_TRANSCRIPTION_LOCALLY' })
	@Action
	async updateTranscription(data: Partial<ITranscription>) {
		const transcriptionRef = db.collection('transcriptions').doc(this.transcription.id);
		await transcriptionRef.update(data);
		return data;
	}

	@Action
	startEditing(payload: { syncMode: boolean; }) {
		if (!this.isEditable) return;
		this.setPersonalHistory({ editing: true });
		log('EDIT_START', { uid: this.currentUser.uid, transcriptionId: this.transcription.id });
		console.log(this.currentTranscription);
		return this.updateTranscription({
			status: 'editing',
			prevStatus: this.currentTranscription.status,
			tempEditedBy: this.currentUser.uid,
			tempText: this.currentTranscription.text,
			timestamp: firebase.firestore.FieldValue.serverTimestamp(),
			syncMode: payload.syncMode
		});
	}

	@Action
	async completeEditing(payload: {
		isComplete: boolean;
		share: boolean;
		requestReview: boolean;
		comment: string;
		count: number;
	}) {
		if (!this.isCurrentUserEditing) return;
		const uid = this.currentUser.uid;
		const status: editingStatus = payload.isComplete ? 'completed' : 'initiated';
		const transcription = {
			status: status,
			editedBy: uid,
			text: this.transcription.tempText,
			tempText: this.transcription.tempText,
			timestamp: firebase.firestore.FieldValue.serverTimestamp(),
			share: payload.share,
			requestReview: payload.requestReview
		};
		await this.updateTranscription(transcription);
		const history: ITranscriptionHistory = {
			entryId: this.entry.id,
			transcriptionId: this.transcription.id,
			index: this.transcription.index,
			status: status,
			uid: uid,
			text: this.transcription.tempText,
			timestamp: firebase.firestore.FieldValue.serverTimestamp(),
			requestReview: payload.requestReview,
			share: payload.share,
			comment: payload.comment,
			count: payload.count,
			likers: {}
		};
		await this.finalizeAnnotations();
		await this.setPersonalHistory({ editing: false });
		log('EDIT_END', { uid, transcriptionId: this.transcription.id });
		return await db.collection('transcriptions').doc(this.transcription.id).collection('history').add(history);
	}

	@Action
	async setPersonalHistory(payload: { editing: boolean; }) {
		// set personal history
		const uid = this.context.rootState.auth.user.uid;
		const personalHistoryId = `${uid}_${this.transcription.id}`;
		const personalHistory: PersonalHistory = {
			uid: uid,
			entryId: this.entry.id,
			label: this.entry.label,
			index: this.transcription.index,
			transcriptionId: this.transcription.id,
			editing: payload.editing,
			timestamp: firebase.firestore.FieldValue.serverTimestamp()
		};
		return await db.collection('personalHistory').doc(personalHistoryId).set(personalHistory);
	}

	@Action
	discardEdit() {
		if (!this.isCurrentUserEditing) return;
		this.removeTempAnnotations();
		this.setPersonalHistory({ editing: false });
		const uid = this.context.rootState.auth.user.uid;
		log('EDIT_DISCARD', { uid, transcriptionId: this.transcription.id });
		return this.updateTranscription({ status: this.currentTranscription.prevStatus || 'default' });
	}

	@Action({ commit: 'UPDATE_TRANSCRIPTION_LOCALLY' })
	setTempText(text: string) {
		return { tempText: text };
	}

	@Action
	saveAnnotation(payload: { anno: IAnnotation; }) {
		if (payload.anno.id) {
			return db.collection('annotations').doc(payload.anno.id).update(payload.anno);
		} else {
			return db.collection('annotations').add(payload.anno);
		}
	}

	@Action
	async removeTempAnnotations() {
		const tempAnnotations = await db
			.collection('annotations')
			.where('transcriptionId', '==', this.transcription.id)
			.where('isTemp', '==', true)
			.get();
		tempAnnotations.forEach((doc) => doc.ref.delete());
	}

	@Action
	async finalizeAnnotations() {
		const tempAnnotations = await db
			.collection('annotations')
			.where('transcriptionId', '==', this.transcription.id)
			.where('isTemp', '==', true)
			.get();
		tempAnnotations.forEach((doc) => doc.ref.update({ isTemp: false }));
	}

	@Action
	async removeAnnotation(payload: { id: string; }) {
		return await db.collection('annotations').doc(payload.id).delete();
	}

	@Action
	addHistory(history: ITranscriptionHistory) {
		return db.collection('transcriptions').doc(this.transcription.id).collection('history').add(history);
	}
}
